import React, { useState } from 'react';
import { Bell, BellOff, AlertCircle, CheckCircle2, Loader2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function PushNotificationSettings() {
    const { token } = useAuth();
    const [testLoading, setTestLoading] = useState(false);
    const {
        isSupported,
        isSubscribed,
        permission,
        loading,
        error,
        subscribe,
        unsubscribe
    } = usePushNotifications(token);

    const handleToggle = async () => {
        if (isSubscribed) {
            const success = await unsubscribe();
            if (success) {
                toast.success('Push notifications disabled');
            }
        } else {
            const success = await subscribe();
            if (success) {
                toast.success('Push notifications enabled');
            } else if (permission === 'denied') {
                toast.error('Please enable notifications in your browser settings');
            }
        }
    };

    const handleTestNotification = async () => {
        setTestLoading(true);
        try {
            const response = await fetch(`${API}/api/push/test`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                toast.success('Test notification sent! Check your device.');
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to send test notification');
            }
        } catch (err) {
            toast.error('Failed to send test notification');
        } finally {
            setTestLoading(false);
        }
    };

    if (!isSupported) {
        return (
            <Card className="border-amber-200 bg-amber-50" data-testid="push-not-supported">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        Push Notifications
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-amber-800">
                        Push notifications are not supported in your browser. 
                        Try using Chrome, Firefox, or Edge for the best experience.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="push-notification-settings">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                    {isSubscribed ? (
                        <Bell className="w-5 h-5 text-green-600" />
                    ) : (
                        <BellOff className="w-5 h-5 text-muted-foreground" />
                    )}
                    Push Notifications
                </CardTitle>
                <CardDescription>
                    Receive instant updates about bookings, events, and community news
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="font-medium text-sm">Enable Notifications</p>
                        <p className="text-xs text-muted-foreground">
                            {isSubscribed 
                                ? 'You will receive push notifications' 
                                : 'Turn on to receive updates'}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        <Switch
                            checked={isSubscribed}
                            onCheckedChange={handleToggle}
                            disabled={loading || permission === 'denied'}
                            data-testid="push-toggle"
                        />
                    </div>
                </div>

                {error && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>{error}</p>
                    </div>
                )}

                {permission === 'denied' && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium">Notifications Blocked</p>
                            <p className="text-xs mt-1">
                                You've blocked notifications for this site. To enable them, 
                                click the lock icon in your browser's address bar and allow notifications.
                            </p>
                        </div>
                    </div>
                )}

                {isSubscribed && (
                    <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="font-medium">Notifications Active</p>
                            <p className="text-xs mt-1">
                                You'll receive updates about activity bookings, service completions, 
                                community notices, and more.
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-3 bg-white hover:bg-green-100 border-green-300"
                                onClick={handleTestNotification}
                                disabled={testLoading}
                                data-testid="test-notification-btn"
                            >
                                {testLoading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                Send Test Notification
                            </Button>
                        </div>
                    </div>
                )}

                <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                        You can change this setting at any time. Notifications will only be sent 
                        for important updates relevant to you.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}

export default PushNotificationSettings;
